import events from 'events-bus';
import { validateCartPayload, findArticleById } from '../utils/cartUtils.js';
import { sendAdobeEvent } from './adobeService.js';
import { computeSyncedCart, getCart, changeArticleQuantity, deleteSavingsPlanArticle, addSavingsPlanArticle } from './cartService.js';
import { getSid } from './shopService.js';
import { getCartApiVersion } from './stateService.js';

/* eslint-disable @typescript-eslint/no-explicit-any */
const staticData = {};
const staticSetters = {};
const setStaticSid = (sid) => {
    staticData.sid = sid;
};
const setStaticCart = async (cart, stage, isOriginCartProvider) => {
    if (!isOriginCartProvider) {
        staticData.cart = cart;
    }
    else {
        staticData.cart = await computeSyncedCart(staticData.cart, cart, stage, staticData.sid);
    }
};
const setStaticSetters = ({ setSid, setCart, setActionInfo }) => {
    staticSetters.setSid = setSid;
    staticSetters.setCart = setCart;
    staticSetters.setActionInfo = setActionInfo;
};
const registerEventsListeners = (stage) => {
    events.on('GET-CART-ID-v1', async (_, request) => {
        if (staticData.sid) {
            postEventGetCartIdSuccess(staticData.sid);
        }
        else {
            try {
                const sid = await getSid(stage);
                staticData.sid = sid;
                staticSetters.setSid(sid);
                postEventGetCartIdSuccess(staticData.sid, request);
            }
            catch (error) {
                postEventGetCartIdFailure(error, request);
            }
        }
    });
    events.on('GET-CART-v1', async (_, request) => {
        if (!staticData.sid) {
            throw new Error('Invalid cart state. GET-CART-v1 event received before cart provider is initialized.');
        }
        const cartVersion = request?.cartVersion ?? (await getCartApiVersion(stage));
        if (!staticData.cartApiVersion) {
            staticData.cartApiVersion = cartVersion;
        }
        if (staticData.cart && staticData.cartApiVersion === cartVersion) {
            await postEventGetCartSuccess({ cart: staticData.cart, request });
        }
        else {
            try {
                const response = await getCart({
                    stage,
                    sid: staticData.sid,
                    cartVersion,
                });
                staticData.cart = response;
                staticSetters.setCart(response, true);
                staticData.cartApiVersion = cartVersion;
                await postEventGetCartSuccess({ cart: staticData.cart, request });
            }
            catch (error) {
                postEventGetCartFailure(error, request);
            }
        }
    });
    events.on('ADD-TO-CART-v1', async (_, request) => {
        if (!staticData.sid || !staticData.cart) {
            throw new Error('Invalid cart state. ADD-TO-CART-v1 event received before cart provider is initialized.');
        }
        const cartVersion = request.cartVersion ?? (await getCartApiVersion(stage));
        const error = validateCartPayload(request, cartVersion);
        if (error) {
            throw new Error(error);
        }
        const { sid, cart: previousCart } = staticData;
        const { articleId, quantity, articleType = 'STANDARD', voucherApplied, voucherChannel, trigger, subscriptionSelected, offerIds, } = request;
        let cart = undefined;
        let status = undefined;
        if (!staticData.cartApiVersion || staticData.cartApiVersion !== cartVersion) {
            staticData.cartApiVersion = cartVersion;
        }
        if (articleType === 'STANDARD') {
            try {
                const changeArticleQuantityRequest = {
                    articleId,
                    quantity,
                    voucherChannel,
                    voucherApplied,
                    subscriptionSelected,
                };
                if (staticData.cartApiVersion === 'v2' && offerIds) {
                    changeArticleQuantityRequest.offerIds = offerIds;
                }
                const response = await changeArticleQuantity({
                    stage,
                    sid,
                    request: changeArticleQuantityRequest,
                    previousQuantity: findArticleById(previousCart, request.articleId)?.quantity ?? 0,
                    cartVersion,
                });
                cart = response.cart;
                status = response.status;
                if (trigger) {
                    staticSetters.setActionInfo({
                        quantity: findArticleById(cart, request.articleId)?.quantity ?? 0,
                        status,
                        popover: { trigger },
                    });
                }
            }
            catch (error) {
                if (trigger) {
                    staticSetters.setActionInfo({
                        error: error,
                        quantity: request.quantity || 0,
                        popover: { trigger },
                    });
                }
                console.error(error);
                postEventAddToCartFailure(error, request);
            }
        }
        else {
            try {
                if (quantity === 0) {
                    cart = await deleteSavingsPlanArticle({
                        stage,
                        sid,
                        cartVersion,
                    });
                }
                else {
                    cart = await addSavingsPlanArticle({
                        stage,
                        sid,
                        articleId,
                        cartVersion,
                    });
                }
            }
            catch (error) {
                console.error(error);
                postEventAddToCartFailure(error, request);
            }
        }
        if (cart) {
            // Article before update
            const articleBeforeUpdate = findArticleById(staticData.cart, articleId);
            const quantityBeforeUpdate = articleBeforeUpdate?.quantity || 0;
            // Article after update
            const articleAfterUpdate = articleBeforeUpdate ?? findArticleById(cart, articleId);
            if (quantityBeforeUpdate !== quantity && articleAfterUpdate) {
                let quantityAdded = 0;
                if (status === 'ADDED') {
                    quantityAdded = quantity - quantityBeforeUpdate;
                }
                let isPersonalized = !!articleAfterUpdate?.articleBasedVoucher?.isPersonalized;
                let articleForEventPrice = articleAfterUpdate?.price;
                if (staticData.cartApiVersion === 'v2') {
                    isPersonalized = !!articleAfterUpdate?.lineItemDiscounts
                        ?.articleBasedDiscount?.isPersonalized;
                    articleForEventPrice = articleAfterUpdate?.price?.value;
                }
                sendAdobeEvent({
                    price: articleForEventPrice,
                    shopId: articleAfterUpdate?.shopId,
                    psId: articleAfterUpdate?.psId,
                    bestPriceArticle: articleAfterUpdate?.bestPriceArticle,
                    sellerType: articleAfterUpdate?.sellerType ?? '1P',
                }, cart.cartId, quantityAdded, isPersonalized);
            }
            staticData.cart = cart;
            staticSetters.setCart(cart, true);
            await postEventAddToCartSuccess({ cart, stage, status, request: { ...request, status } });
        }
    });
};
const postEventAddToCartSuccess = async ({ cart, stage, status, request, isOriginCartProvider, }) => {
    if (isOriginCartProvider) {
        const syncedCart = await computeSyncedCart(staticData.cart, cart, stage, staticData.sid);
        postAndDispatch('ADD-TO-CART-SUCCESS-v1', { response: { ...syncedCart, status }, request });
    }
    else {
        postAndDispatch('ADD-TO-CART-SUCCESS-v1', { response: { ...cart, status }, request });
    }
};
const postEventAddToCartFailure = (error, request) => {
    postAndDispatch('ADD-TO-CART-FAILURE-v1', { error, request });
};
const postEventGetCartSuccess = async ({ cart, stage, request, isOriginCartProvider, }) => {
    if (isOriginCartProvider) {
        const syncedCart = await computeSyncedCart(staticData.cart, cart, stage, staticData.sid);
        postAndDispatch('GET-CART-SUCCESS-v1', { response: syncedCart, request });
    }
    else {
        postAndDispatch('GET-CART-SUCCESS-v1', { response: cart, request });
    }
};
const postEventGetCartFailure = (error, request) => {
    postAndDispatch('GET-CART-FAILURE-v1', { error, request });
};
const postEventGetCartIdSuccess = (sid, request) => {
    postAndDispatch('GET-CART-ID-SUCCESS-v1', { response: sid, request });
};
const postEventGetCartIdFailure = (error, request) => {
    postAndDispatch('GET-CART-ID-FAILURE-v1', { error, request });
};
const postAndDispatch = (eventName, detail) => {
    events.post(eventName, detail);
    document.dispatchEvent(createEvent(eventName, { detail }));
};
const createEvent = (eventName, eventInitDict) => {
    return new CustomEvent(eventName, eventInitDict);
};

export { postAndDispatch, postEventAddToCartFailure, postEventAddToCartSuccess, postEventGetCartFailure, postEventGetCartIdSuccess, postEventGetCartSuccess, registerEventsListeners, setStaticCart, setStaticSetters, setStaticSid };
//# sourceMappingURL=eventsService.js.map
